import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Select from "react-select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createCategoryByAdmin } from "../../services/categoryService";
import { ClipLoader } from "react-spinners";

const categorySchema = z.object({
  name: z.string().min(2, "Category name required."),
  isFeatured: z.boolean(),
});

interface AddCategoryModalProps {
  onClose: () => void;
}

const AddCategoryModal = ({ onClose }: AddCategoryModalProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
      isFeatured: false,
    },
  });

  const mutation = useMutation({
    mutationFn: createCategoryByAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      onClose();
    },
  });

  const onSubmit = (data: { name: string; isFeatured: boolean }) => {
    mutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 p-2 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl mb-4">New Category</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Category Name
            <input
              {...register("name")}
              type="text"
              className="w-full mt-1 p-2 border rounded-md"
            />
            {errors.name && (
              <p className="text-sm text-rose-500 mt-1">
                {errors.name.message}
              </p>
            )}
          </label>

          <label className="block mb-2 text-sm font-medium text-gray-700">
            Is Featured
            <Select
              options={[
                { value: true, label: "True" },
                { value: false, label: "False" },
              ]}
              defaultValue={{ value: false, label: "False" }}
              onChange={(selectedOption) =>
                setValue("isFeatured", selectedOption?.value ?? false)
              }
            />
            {errors.isFeatured && (
              <p className="text-sm text-rose-500 mt-1">
                {errors.isFeatured.message}
              </p>
            )}
          </label>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              className="px-4 py-2 text-rose-500 mt-1 rounded-lg text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded-lg border flex items-center justify-center"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? <ClipLoader size={20} /> : "Add Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCategoryModal;
